/**
 * Universal listener for backend "syncdata" WebSocket messages.
 * Handles real-time sync when models are created/updated/deleted by other users (or same scope).
 *
 * Message codes (from backend, e.g. signals.py):
 * - post   – model was created by a user from same scope
 * - put    – model was updated by a user from same scope
 * - delete – model was deleted by a user from same scope
 * - action.fetch – e.g. from section duplication; can be treated like post in some cases
 *
 * Initialize once (e.g. in app root or SocketProvider) and register handlers for your models.
 */

export type SyncdataCode = 'post' | 'put' | 'delete' | 'action.fetch';

export interface SyncdataMessage<T = Record<string, unknown>> {
  type: 'syncdata';
  app?: string;
  model: string;
  code: SyncdataCode;
  data: T & { id: number };
  id?: number;
}

export type SyncdataUnsubscribe = () => void;

export type SubscribeToEvent = (
  eventName: string,
  callback: (data: unknown) => void,
) => SyncdataUnsubscribe;

export interface SyncdataHandlers {
  /** Map backend (django) model name → frontend model name. Return undefined to ignore. */
  getModelName?: (djangoName: string) => string | undefined;
  /** Called for syncdata with code 'post'. You may skip if the model was already created by current client. */
  onPost?: (modelName: string, message: SyncdataMessage) => void | Promise<void>;
  /** Called for syncdata with code 'put'. Update local instance from message.data. */
  onPut?: (modelName: string, message: SyncdataMessage) => void | Promise<void>;
  /** Called for syncdata with code 'delete'. Remove or invalidate local instance. */
  onDelete?: (modelName: string, message: SyncdataMessage) => void | Promise<void>;
  /** Called for syncdata with code 'action.fetch' (e.g. section duplication). */
  onActionFetch?: (modelName: string, message: SyncdataMessage) => void | Promise<void>;
}

/** Default django model name → frontend model name mapping (extend as needed). */
export const DEFAULT_MODELS_MAP: Record<string, string> = {
  ahunittunnel: 'tunnel.model',
  ahusection: 'section.model',
  ahucoilsection: 'component.coil.model',
  ahufansection: 'component.fan.model',
  ahufiltersection: 'component.filter.model',
  ahuenergywheelsection: 'component.energy_wheel.model',
  ahuaccessoryhood: 'model.accessory.hood',
  ahuaccessoryopening: 'model.accessory.opening',
  ahuaccessoryaccessdoor: 'model.accessory.access_door',
  ahuaccessorydamper: 'model.accessory.damper',
  ahuaccessorydrainpan: 'model.accessory.drain_pan',
  ahuuserdefinedaccessory: 'model.accessory.user_defined',
  ksdmproject: 'project',
};

function isSyncdataMessage(msg: unknown): msg is SyncdataMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as SyncdataMessage).type === 'syncdata' &&
    typeof (msg as SyncdataMessage).model === 'string' &&
    typeof (msg as SyncdataMessage).code === 'string'
  );
}

export interface SyncdataListenerOptions {
  /** Socket event name used by backend for streamed data. Default: 'data'. */
  eventName?: string;
  /** Optional: skip handling while this returns true (e.g. modal open). */
  shouldSkip?: () => boolean;
  /** Model name mapping. Default: DEFAULT_MODELS_MAP. */
  modelsMap?: Record<string, string>;
}

/**
 * Creates a syncdata listener that subscribes to the socket and routes syncdata
 * messages to the provided handlers.
 *
 * @param subscribe - Function to subscribe to socket events (e.g. from useSocket().subscribeToEvent)
 * @param handlers - Handlers for post/put/delete/action.fetch
 * @param options - Optional event name, shouldSkip, and models map
 * @returns Unsubscribe function to stop listening
 */
export function createSyncdataListener(
  subscribe: SubscribeToEvent,
  handlers: SyncdataHandlers,
  options: SyncdataListenerOptions = {},
): SyncdataUnsubscribe {
  const {
    eventName = 'data',
    shouldSkip,
    modelsMap = DEFAULT_MODELS_MAP,
  } = options;

  function getModelName(djangoName: string): string | undefined {
    const name = modelsMap[djangoName];
    if (!name && djangoName) {
      console.warn(`[SyncdataListener] Unknown django model name: ${djangoName}`);
    }
    return name;
  }

  function onMessage(message: unknown): void {
    if (!isSyncdataMessage(message)) return;
    if (shouldSkip?.()) return;

    const modelName = getModelName(message.model);
    if (!modelName) return;

    switch (message.code) {
      case 'post':
        void handlers.onPost?.(modelName, message);
        break;
      case 'put':
        void handlers.onPut?.(modelName, message);
        break;
      case 'delete':
        void handlers.onDelete?.(modelName, message);
        break;
      case 'action.fetch':
        void handlers.onActionFetch?.(modelName, message);
        break;
      default:
        console.warn(`[SyncdataListener] Unhandled code: ${(message as SyncdataMessage).code}`);
    }
  }

  return subscribe(eventName, onMessage);
}
